(() => {
  let activeMedia = null;
  const highResolutionLoads = new WeakMap();

  const getCard = (target) => target instanceof Element
    ? target.closest('.product-card-wrapper')
    : null;

  const getMedia = (card) => card?.querySelector('[data-amaca-product-zoom]') || null;

  const containsPointer = (media, event) => {
    const bounds = media.getBoundingClientRect();
    return event.clientX >= bounds.left
      && event.clientX <= bounds.right
      && event.clientY >= bounds.top
      && event.clientY <= bounds.bottom;
  };

  const updateFocus = (media, event) => {
    const bounds = media.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const x = Math.min(100, Math.max(0, ((event.clientX - bounds.left) / bounds.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - bounds.top) / bounds.height) * 100));
    media.style.setProperty('--amaca-zoom-x', `${x.toFixed(2)}%`);
    media.style.setProperty('--amaca-zoom-y', `${y.toFixed(2)}%`);
  };

  const loadHighResolution = (media) => {
    if (media.hasAttribute('data-amaca-zoom-ready')) return Promise.resolve();
    if (highResolutionLoads.has(media)) return highResolutionLoads.get(media);

    const image = media.querySelector('.media > img:first-child[data-amaca-zoom-src]');
    const source = image?.dataset.amacaZoomSrc;
    if (!image || !source) return Promise.resolve();

    media.setAttribute('data-amaca-zoom-loading', '');
    const load = new Promise((resolve) => {
      const highResolutionImage = new Image();
      highResolutionImage.decoding = 'async';
      highResolutionImage.onload = async () => {
        try {
          if (highResolutionImage.decode) await highResolutionImage.decode();
        } catch (_error) {
          // The decoded file is still usable if explicit decoding is unavailable.
        }

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = source;
        media.removeAttribute('data-amaca-zoom-loading');
        media.setAttribute('data-amaca-zoom-ready', '');
        resolve();
      };
      highResolutionImage.onerror = () => {
        media.removeAttribute('data-amaca-zoom-loading');
        resolve();
      };
      highResolutionImage.src = source;
    });

    highResolutionLoads.set(media, load);
    return load;
  };

  const activate = (media, event) => {
    if (!media || event.pointerType === 'touch') return;
    if (activeMedia && activeMedia !== media) activeMedia.removeAttribute('data-amaca-zoom-active');
    activeMedia = media;
    updateFocus(media, event);
    media.setAttribute('data-amaca-zoom-active', '');
    loadHighResolution(media).then(() => {
      if (activeMedia === media) updateFocus(media, event);
    });
  };

  const deactivate = (media) => {
    if (!media) return;
    media.removeAttribute('data-amaca-zoom-active');
    media.style.removeProperty('--amaca-zoom-x');
    media.style.removeProperty('--amaca-zoom-y');
    if (activeMedia === media) activeMedia = null;
  };

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') {
      document.documentElement.classList.remove('amaca-product-zoom-enabled');
      deactivate(activeMedia);
      return;
    }

    document.documentElement.classList.add('amaca-product-zoom-enabled');

    const media = getMedia(getCard(event.target));
    if (media && containsPointer(media, event)) {
      if (activeMedia !== media) activate(media, event);
      else updateFocus(media, event);
      return;
    }

    deactivate(activeMedia);
  }, { passive: true });

  document.addEventListener('pointerleave', () => deactivate(activeMedia), { passive: true });

})();
